import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../../utils/httpRequest";
import { Quotation } from "../../../stores/CoursePreviewStore/Types";
import { CoursesResource } from "../../../stores/CoursesStore/Types";
import {
  spotlight,
  featuredWomen,
  generalPool,
} from "../../Home/components/Instructors/thoughtLeaders";
import { NewTeamForm } from "./NewTeamForm";
import TrustedLogos from "./components/TrustedLogos";

const clientLogos = [
  {
    src: require("../../../../../assets/images/teams/logos/abinbev.png"),
    alt: "AB InBev",
    className: "teams-logo-abinbev",
  },
  {
    src: require("../../../../../assets/images/teams/logos/ogilvy.png"),
    alt: "Ogilvy",
    className: "teams-logo-ogilvy",
  },
  {
    src: require("../../../../../assets/images/teams/logos/amazon.png"),
    alt: "Amazon",
    className: "teams-logo-amazon",
  },
  {
    src: require("../../../../../assets/images/teams/logos/apple.png"),
    alt: "Apple",
    className: "teams-logo-apple",
  },
  {
    src: require("../../../../../assets/images/teams/logos/google.png"),
    alt: "Google",
    className: "teams-logo-google",
  },
  {
    src: require("../../../../../assets/images/teams/logos/accenture.png"),
    alt: "Accenture",
    className: "teams-logo-accenture",
  },
  {
    src: require("../../../../../assets/images/teams/logos/hsbc.png"),
    alt: "HSBC",
    className: "teams-logo-hsbc",
  },
  {
    src: require("../../../../../assets/images/teams/logos/disney.png"),
    alt: "Disney",
    className: "teams-logo-disney",
  },
];

const aidenImage = require("../../../../../assets/images/teams/facelift/Aiden_Connolly-8d89478e-803d-4e83-a7b5-c320bbefcebc.png");
const googleLogo = require("../../../../../assets/images/teams/facelift/Google_logo-c4e5a0de-ab68-4d63-b161-b0106196ecac.png");
const namibiaLogo = require("../../../../../assets/images/teams/facelift/Namibia_Breweries_Logo-c24384d7-1951-449e-968b-e4d0679cd6dc.png");
const ogilvyLogo = require("../../../../../assets/images/teams/facelift/Ogilvy-29a18be3-3754-41e2-bde4-6cd954c45d5a.png");
const toyotaLogo = require("../../../../../assets/images/teams/facelift/Toyota_EU-bcfafe21-05da-42b0-b718-8b6e2be99c63.png");

type ThoughtLeader = {
  name: string;
  role: string;
  avatar: string;
};

const allThoughtLeaders = [...spotlight, ...featuredWomen, ...generalPool];

const pickLeader = (name: string) =>
  allThoughtLeaders.find((leader) => leader.name === name);

const teamsThoughtLeaders = [
  "Rory Sutherland",
  "Dan Ariely",
  "Sir John Hegarty",
  "Les Binet",
  "Josy Paul",
  "Keith Reinhard",
  "Meik Wiking",
  "Tara Austin",
  "Benedict Evans",
]
  .map(pickLeader)
  .filter(Boolean) as ThoughtLeader[];

const curriculum = [
  {
    icon: "icon-brain",
    title: "Behavioural Science & Psychology",
    body: "Understand why people do what they do and how to positively influence their decisions and habits.",
  },
  {
    icon: "icon-leadership",
    title: "Leadership & Wellbeing",
    body: "Tools to manage teams effectively, boost morale and productivity, and drive lasting organisational success.",
  },
  {
    icon: "icon-strategy",
    title: "Marketing, Strategy & Sales",
    body: "Proven frameworks to build standout campaigns, tell better stories, and scale your brand.",
  },
  {
    icon: "icon-ai-spark",
    title: "AI & Innovation",
    body: "Learn how to use and apply AI and innovation to stay ahead.",
  },
];

const testimonials = [
  {
    logo: ogilvyLogo,
    logoAlt: "Ogilvy logo",
    client: "Ogilvy",
    tag: "Agency",
    quote:
      "A business learning platform with creative flair. Easy to set up, provides healthy competition, and helps in both IQ and EQ. Highly recommend it.",
    name: "Rob H.",
    role: "Managing Director",
  },
  {
    logo: namibiaLogo,
    logoAlt: "Namibia Breweries logo",
    client: "Namibia Breweries",
    tag: "FMCG",
    quote:
      "The first training where everyone finished the courses without us chasing them!",
    name: "Samanthe H.",
    role: "HR Director",
  },
  {
    logo: googleLogo,
    logoAlt: "Google logo",
    client: "Google",
    tag: "Tech",
    quote:
      "An engaging suite of courses, great for lower attention spans, and fits perfectly around our busy schedule. The leaderboards help make our remote workers feel connected.",
    name: "James R.",
    role: "Team Lead",
  },
];

const comparisonRows = [
  ["Team completion rate", "80%+ average", "5 to 15%"],
  [
    "Taught by published authors / practitioners",
    "Rory, Dan, Sir John and 20+ others",
    "Freelance trainers",
  ],
  [
    "Human-marked assignments with feedback",
    "24-hour turnaround",
    "Multiple-choice quiz",
  ],
  [
    "Personalised by role and sector",
    "AI-adapted per learner",
    "One-size-fits-all",
  ],
  ["CPD-accredited certificates", "Every course", "Completion badge"],
  [
    "Practical AI tools to apply the learning",
    "Briefing tool, creative thinking tools and more",
    "Watch-only content",
  ],
  [
    "Dedicated success manager",
    "Standard for all customers",
    "Chatbot helpdesk",
  ],
];

const faqs = [
  {
    question: "What's the minimum team size?",
    answer: (
      <p>
        Team plans start at 5 learners. Below that, individual All Access Passes
        are usually a better fit and your group can still share a community
        space.
      </p>
    ),
  },
  {
    question: "How does pricing work?",
    answer: (
      <>
        <p>
          Pricing scales with team size only. There are no feature tiers. Every
          customer gets the full platform, including SSO, API access, custom
          learning paths and a dedicated success manager.
        </p>
        <p>
          We'll send a proposal within 48 hours of an initial call. No haggling,
          no loss-leader tricks.
        </p>
      </>
    ),
  },
  {
    question: "How quickly can we onboard?",
    answer: (
      <>
        <p>
          Standard onboarding is 3 to 5 working days from contract to go-live.
          If you need to move faster, we can have your team learning within{" "}
          <strong>24 to 48 hours</strong> of contract signing.
        </p>
        <p>
          Custom learning paths and SSO setup typically add 1 to 2 weeks if you
          want them ready at launch.
        </p>
      </>
    ),
  },
  {
    question: "Do you offer SSO and enterprise security?",
    answer: (
      <p>
        Yes, for every customer. SAML 2.0 SSO, SCIM user provisioning and SOC 2
        Type II compliance are standard regardless of team size. We support
        Okta, Azure AD, Google Workspace and OneLogin out of the box.
      </p>
    ),
  },
  {
    question: "Can we create custom learning paths for different teams?",
    answer: (
      <p>
        Yes, that's how most customers use us. We'll work with you to build
        role-specific tracks. Marketing gets one path, creative another,
        leadership another, all with shared community space and consistent
        certification.
      </p>
    ),
  },
  {
    question: "Are the certificates CPD-accredited?",
    answer: (
      <p>
        Yes. Every course is CPD-accredited with formal certification on
        completion. CPD hours count toward continuing professional development
        requirements across marketing, advertising, financial services,
        consulting and most other professional industries.
      </p>
    ),
  },
  {
    question: "Do you run in-person or virtual workshops?",
    answer: (
      <>
        <p>
          Yes. We run bespoke workshops alongside our online courses, designed
          to land specific frameworks deeper with your team.
        </p>
        <p>
          <a href="/workshops">See our workshop formats</a>, or email{" "}
          <a href="mailto:groundcontrol@42courses.com?subject=Workshop%20enquiry">
            groundcontrol@42courses.com
          </a>{" "}
          to discuss what you need.
        </p>
      </>
    ),
  },
  {
    question: "Can we book one of your thought leaders for a talk?",
    answer: (
      <p>
        Yes, we can connect you with our instructors for talks, keynotes and
        panel events. Email{" "}
        <a href="mailto:groundcontrol@42courses.com?subject=Speaker%20enquiry">
          groundcontrol@42courses.com
        </a>{" "}
        with what you're thinking and we'll come back with options.
      </p>
    ),
  },
];

const SvgDefinitions = () => (
  <svg
    width="0"
    height="0"
    style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <g id="teams-blob-1" transform="translate(-100,-100)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z"></path>
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z"></path>
      </g>
      <g id="teams-blob-2" transform="translate(100,-100) scale(-1,1)">
        <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z"></path>
        <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z"></path>
      </g>

      <symbol id="icon-brain" viewBox="0 0 24 24">
        <path
          d="M15.5 3.5c-1.2 0-2.3.5-3 1.3-.7-.8-1.8-1.3-3-1.3-2.2 0-4 1.8-4 4 0 .4.1.8.2 1.2C4.6 9 3.5 10.3 3.5 12c0 1.4.9 2.6 2.1 3.2-.1.4-.1.8-.1 1.3 0 2.2 1.8 4 4 4 1.1 0 2.1-.4 2.8-1.1.7.7 1.7 1.1 2.8 1.1 2.2 0 4-1.8 4-4 0-.4-.1-.8-.1-1.3 1.2-.6 2.1-1.8 2.1-3.2 0-1.7-1.1-3-2.2-3.3.1-.4.2-.8.2-1.2 0-2.2-1.8-4-4-4zm-3 14v-11"
          stroke="#C4841D"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        ></path>
        <path
          d="M9 7.5C8 7 7 8 7.5 9M15 7.5C16 7 17 8 16.5 9M8 12.5C7 12 6 13 6.5 14M16 12.5C17 12 18 13 17.5 14M10 16c-1 0-2 1-1 2M14 16c1 0 2 1 1 2"
          stroke="#C4841D"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        ></path>
      </symbol>
      <symbol id="icon-leadership" viewBox="0 0 24 24">
        <path
          d="M12 2.5 L14.59 9.51 L22 9.99 L16.18 14.62 L18.18 21.5 L12 17.77 L5.82 21.5 L7.82 14.62 L2 9.99 L9.41 9.51 Z"
          fill="#C4841D"
        ></path>
      </symbol>
      <symbol id="icon-strategy" viewBox="0 0 24 24">
        <path
          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"
          fill="#C4841D"
        ></path>
        <circle cx="12" cy="12" r="2" fill="#C4841D"></circle>
      </symbol>
      <symbol id="icon-ai-spark" viewBox="0 0 24 24">
        <path
          d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z"
          fill="#C4841D"
        ></path>
        <path
          d="M5.5 2 L6 4.5 L8.5 5 L6 5.5 L5.5 8 L5 5.5 L2.5 5 L5 4.5 Z"
          fill="#C4841D"
        ></path>
        <path
          d="M18.5 15 L19 17.5 L21.5 18 L19 18.5 L18.5 21 L18 18.5 L15.5 18 L18 17.5 Z"
          fill="#C4841D"
        ></path>
      </symbol>
    </defs>
  </svg>
);

const HeroSection = () => (
  <section className="teams-hero" aria-labelledby="teams-hero-title">
    <div className="teams-hero-pattern" aria-hidden="true">
      <svg
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <svg x="100%" y="0" overflow="visible">
          <path
            d="M -210 0 A 215 215 0 0 0 0 260 L 0 0 Z"
            fill="#F3EC7A"
            opacity="0.85"
          ></path>
        </svg>
        <svg x="10%" y="20%" overflow="visible">
          <use
            href="#teams-blob-1"
            fill="#B798F2"
            transform="scale(1.7)"
            opacity="0.15"
          ></use>
        </svg>
        <svg x="85%" y="60%" overflow="visible">
          <use
            href="#teams-blob-1"
            fill="#FFA5BC"
            transform="scale(2.1)"
            opacity="0.13"
          ></use>
        </svg>
        <svg x="50%" y="-10%" overflow="visible">
          <use
            href="#teams-blob-2"
            fill="#E8EBFF"
            transform="scale(1.4)"
            opacity="0.55"
          ></use>
        </svg>
        <svg x="92%" y="50%" overflow="visible">
          <use
            href="#teams-blob-1"
            fill="#F3EC7A"
            transform="scale(0.9)"
            opacity="0.3"
          ></use>
        </svg>
      </svg>
    </div>
    <div className="teams-container">
      <span className="teams-badge">42courses For Teams</span>
      <h1 id="teams-hero-title">
        Upskill Your Team With <em>The World's Best Minds.</em>
      </h1>
      <p className="teams-tagline">
        Swap generic video platforms for Mini MBAs taught by industry legends.
        Personalised, human-marked, and genuinely fun, delivering an unmatched
        80%+ completion rate.
      </p>
      <div className="teams-cta-row">
        <a
          href="#"
          data-smooth-scroll-to="#demo"
          className="teams-btn teams-btn-blue"
        >
          Get a Quote / Brochure
        </a>
        <a href="/workshops" className="teams-btn teams-btn-outline">
          Browse workshops
        </a>
      </div>
      <div className="teams-stats" aria-label="Key statistics">
        <div>
          <strong>80%+</strong> Avg. Completion Rate
        </div>
        <div>
          <strong>82 NPS</strong> Learner Satisfaction
        </div>
        <div>
          <strong>Human</strong> Marked Assignments
        </div>
      </div>
    </div>
  </section>
);

const CurriculumSection = () => (
  <section
    className="teams-curriculum-section"
    aria-labelledby="curriculum-title"
  >
    <div className="teams-container">
      <div className="teams-section-header">
        <span className="teams-section-eyebrow">What we teach</span>
        <h2 className="teams-section-title" id="curriculum-title">
          The skills that compound, while the tools change.
        </h2>
        <p className="teams-section-sub">
          AI is rewriting the toolbox every six months. These are the human
          skills that don't go out of date, taught by the people who built the
          field.
        </p>
      </div>
      <div className="teams-curr-grid">
        {curriculum.map((item) => (
          <article className="teams-curr-card" key={item.title}>
            <div className="teams-icon" aria-hidden="true">
              <svg>
                <use href={`#${item.icon}`} />
              </svg>
            </div>
            <h4>{item.title}</h4>
            <p>{item.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ThoughtLeaderGrid = () => (
  <div className="teams-tl-grid" aria-label="42courses thought leaders">
    {teamsThoughtLeaders.map((leader) => (
      <div className="teams-tl-avatar" key={leader.name}>
        <img
          src={leader.avatar}
          alt={`${leader.name}, ${leader.role}`}
          loading="lazy"
          width="90"
          height="90"
        />
      </div>
    ))}
  </div>
);

const FeaturesSection = () => (
  <section className="teams-b2b-features" aria-labelledby="features-title">
    <div className="teams-container">
      <div className="teams-section-header">
        <h2 className="teams-section-title" id="features-title">
          How We Drive Real Growth
        </h2>
        <p className="teams-section-sub">
          We combine the science of learning with world-class industry experts
          to create practical training that teams actually use to get better
          results.
        </p>
      </div>

      <div className="teams-feature-row">
        <div className="teams-feature-text">
          <h3>Taught by the people who wrote the book. Literally.</h3>
          <p>
            Your team won't be listening to a monotone voiceover reading bullet
            points. They'll learn directly from industry icons like Dan Ariely,
            Rory Sutherland, and Josy Paul.
          </p>
          <ul>
            <li>Bite-sized, practical lessons that fit around work</li>
            <li>Real-world case studies, not academic fluff</li>
            <li>CPD-accredited Mini MBA certifications</li>
          </ul>
        </div>
        <div className="teams-feature-visual">
          <svg
            className="teams-visual-blob"
            x="50%"
            y="50%"
            overflow="visible"
            aria-hidden="true"
          >
            <use
              href="#teams-blob-2"
              fill="#FFA5BC"
              transform="scale(2.5) rotate(-15)"
              opacity="0.3"
            />
          </svg>
          <div className="teams-mockup teams-tl-mockup">
            <ThoughtLeaderGrid />
          </div>
        </div>
      </div>

      <div className="teams-feature-row">
        <div className="teams-feature-text">
          <h3>Track progress without the micromanagement.</h3>
          <p>
            A beautifully simple dashboard gives you instant visibility over who
            is thriving, who needs a nudge, and where the skills gaps are.
          </p>
          <ul>
            <li>Live team analytics and progress tracking</li>
            <li>Leaderboards to spark healthy competition</li>
            <li>Identify star performers effortlessly</li>
          </ul>
        </div>
        <div className="teams-feature-visual teams-feature-visual-lavender">
          <div className="teams-mockup">
            <div className="teams-leaderboard-head">
              <strong>Team Leaderboard</strong>
              <span>This Month</span>
            </div>
            {[
              ["1", "Sarah Jenkins", "AI for Marketers · 100%", "gold"],
              ["2", "Marcus Wright", "Creative Thinking · 82%", "silver"],
            ].map(([rank, name, course, tone]) => (
              <div className="teams-leaderboard-row" key={rank}>
                <div className={`teams-rank teams-rank-${tone}`}>{rank}</div>
                <div>
                  <div className="teams-leaderboard-name">{name}</div>
                  <div className="teams-leaderboard-course">{course}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="teams-feature-row">
        <div className="teams-feature-text">
          <h3>AI tailored. Human marked.</h3>
          <p>
            Polls and quick-fire questions get the brain warmed up at the start
            of each lesson, but the real work is applied assignments. AI
            personalises the context to each team member's role, and real human
            experts mark every assignment within 24 hours.
          </p>
          <ul>
            <li>Courses personalised to your role and goals</li>
            <li>Access to the practical Learner AI Toolkit</li>
            <li>Human-marked feedback on practical assignments</li>
            <li>Develops both IQ and EQ skills</li>
          </ul>
        </div>
        <div className="teams-feature-visual teams-feature-visual-sunshine">
          <div className="teams-mockup teams-ai-toolkit">
            <h4>Included AI Toolkit</h4>
            {[
              [
                "teams-icon-ai-spark",
                "AI Briefing Helper",
                "Critiques and improves creative briefs instantly",
              ],
              [
                "teams-icon-brain",
                "BeSci Framing Tool",
                "Apply proven psychological principles to problem solving",
              ],
              [
                "teams-icon-strategy",
                "Creative Idea Generator",
                "Unblocks lateral thinking using structured prompts",
              ],
            ].map(([icon, title, body]) => (
              <div className="teams-tool" key={title}>
                <svg aria-hidden="true">
                  <use href={`#${icon}`} />
                </svg>
                <div>
                  <strong>{title}</strong>
                  <span>{body}</span>
                </div>
              </div>
            ))}
            <div className="teams-more-tools">+ more practical AI tools</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CaseStudySection = () => (
  <section className="teams-hero-case" aria-labelledby="herocase-title">
    <div className="teams-container">
      <div className="teams-case-logo" aria-hidden="true">
        <img src={toyotaLogo} alt="" loading="lazy" />
      </div>
      <div className="teams-hero-case-grid">
        <div>
          <div className="teams-eyebrow">Customer Story · Toyota</div>
          <blockquote id="herocase-title">
            "We needed our innovation team genuinely fluent in behavioural
            science and AI, not just exposed to it.{" "}
            <strong>
              340 people completed their learning paths in under 8 weeks at a
              94% completion rate.
            </strong>{" "}
            The frameworks are now showing up in our actual product decisions,
            which is the metric that really matters."
          </blockquote>
          <div className="teams-attribution">
            <img
              className="teams-attr-pic"
              src={aidenImage}
              alt="Aiden Connolly"
              loading="lazy"
              width="56"
              height="56"
            />
            <div>
              <div className="teams-attr-name">Aiden Connolly</div>
              <div className="teams-attr-role">Head of Innovation, Toyota</div>
            </div>
          </div>
        </div>
        <div className="teams-hero-case-metrics" aria-label="Outcome metrics">
          {[
            ["94%", "Completion rate"],
            ["340", "Team members onboarded"],
            ["8 wks", "End-to-end rollout"],
            ["+81 NPS", "Post-programme"],
          ].map(([value, label]) => (
            <div className="teams-hero-case-metric" key={value}>
              <strong>{value}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section
    className="teams-case-grid-section"
    aria-labelledby="testimonials-title"
  >
    <div className="teams-container">
      <div className="teams-section-header">
        <span className="teams-section-eyebrow">What L&D Leaders Say</span>
        <h2 className="teams-section-title" id="testimonials-title">
          Three more teams. Three different outcomes.
        </h2>
      </div>
      <div className="teams-case-grid">
        {testimonials.map((testimonial) => (
          <article className="teams-case-card" key={testimonial.client}>
            <div className="teams-client-line">
              <img
                src={testimonial.logo}
                alt={testimonial.logoAlt}
                loading="lazy"
              />
              <div>
                <div className="teams-client-name">{testimonial.client}</div>
                <div className="teams-client-tag">{testimonial.tag}</div>
              </div>
            </div>
            <blockquote>"{testimonial.quote}"</blockquote>
            <div className="teams-attr">
              <strong>{testimonial.name}</strong>
              {testimonial.role}
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

interface LeadGenProps {
  overviewFile: string;
  fileName: string;
  recaptchaRef: React.RefObject<any>;
  pathName: string;
}

const LeadGenSection = ({
  overviewFile,
  fileName,
  recaptchaRef,
  pathName,
}: LeadGenProps) => (
  <section className="teams-lead-gen" id="demo" aria-labelledby="leadgen-title">
    <div className="teams-container">
      <div className="teams-lead-grid">
        <div className="teams-lead-text">
          <h2 id="leadgen-title">Let us build a custom plan.</h2>
          <p>
            Need more than 50 seats, custom learning paths, or SSO? Drop your
            details below to get a custom quote, arrange a free demo, or set up
            a pilot trial for your team.
          </p>
          <p className="teams-lead-price">
            <span className="teams-lead-price-pill">From as little as £250 per person</span>{" "}
            for a year of every course. The exact figure depends on team size,
            and we quote it plainly.
          </p>
          <ul className="teams-lead-ticks">
            <li>Enterprise volume discounts</li>
            <li>Free demos and pilot trials</li>
            <li>Fast, frictionless onboarding</li>
          </ul>
          <div className="teams-quick-call-box">
            <h4>Just want a quick chat?</h4>
            <p>
              Skip the form and jump straight onto a quick scoping call with our
              team.
            </p>
            <a
              href="https://calendar.app.google/XnYVnoQQhWAenW5G7"
              target="_blank"
              rel="noopener noreferrer"
              className="teams-btn teams-btn-white"
            >
              Book a short call
            </a>
          </div>
        </div>
        <NewTeamForm
          recaptchaRef={recaptchaRef}
          overviewFile={overviewFile}
          fileName={fileName}
          source={pathName}
        />
      </div>
    </div>
  </section>
);

const QuickBuySection = () => (
  <section className="teams-quickbuy-section" aria-labelledby="quickbuy-title">
    <div className="teams-container">
      <div className="teams-quickbuy-box">
        <h3 id="quickbuy-title">In a rush? Self-serve for up to 20 people.</h3>
        <p>
          Skip the call and buy directly. Automatic bulk discounts are applied
          instantly at checkout.
        </p>
        <div className="teams-quickbuy-actions">
          <a href="/passes/year/gift" className="teams-btn teams-btn-blue">
            Buy All Access Passes
          </a>
          <a
            href="/courses/ai-essentials/gift"
            className="teams-btn teams-btn-white"
          >
            Buy Individual Courses
          </a>
        </div>
      </div>
    </div>
  </section>
);

const ComparisonSection = () => (
  <section className="teams-compare-section" aria-labelledby="compare-title">
    <div className="teams-container">
      <div className="teams-section-header">
        <span className="teams-section-eyebrow">The honest comparison</span>
        <h2 className="teams-section-title" id="compare-title">
          42courses vs typical content libraries.
        </h2>
        <p className="teams-section-sub">
          We won't name them by name, but you know the ones we mean.
        </p>
      </div>
      <div className="teams-compare-table">
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">42courses</th>
              <th scope="col">Typical library</th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map(([feature, ours, theirs]) => (
              <tr key={feature}>
                <td>{feature}</td>
                <td>
                  <span className="teams-check">✓</span>
                  {ours}
                </td>
                <td>
                  <span className="teams-cross">✗</span>
                  {theirs}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const FAQSection = () => (
  <section className="teams-faq-section" aria-labelledby="faq-title">
    <div className="teams-container">
      <div className="teams-section-header">
        <span className="teams-section-eyebrow">Frequently asked</span>
        <h2 className="teams-section-title" id="faq-title">
          The questions every L&D team asks us.
        </h2>
      </div>
      <div className="teams-faq-list">
        {faqs.map((faq) => (
          <details className="teams-faq-item" key={faq.question}>
            <summary>{faq.question}</summary>
            <div className="teams-answer">{faq.answer}</div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

interface Props {
  selectedCurrency: string;
  location: {
    pathname: string;
  };
}

interface State {
  quote: Quotation;
  courses: CoursesResource;
  customBundlePrice: number;
  overviewFile: string;
  filename: string;
  loading: boolean;
}

class TeamPlans extends Component<Props, State> {
  private recaptchaRef = React.createRef<any>();

  constructor(props) {
    super(props);

    this.state = {
      quote: null,
      courses: [],
      customBundlePrice: null,
      overviewFile: "",
      filename: "",
      loading: true,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);

    this.getTeamPayload();
  }

  componentDidUpdate(prevProps) {
    window.scrollTo(0, 0);
    if (
      prevProps.selectedCurrency !== "" &&
      this.props.selectedCurrency !== prevProps.selectedCurrency
    ) {
      this.getTeamPayload();
    }
  }

  getTeamPayload = () => {
    this.setState({ loading: true });

    return getRequest("/api/pages/teams.json")
      .then((response: AxiosResponse) => {
        const { quote, courses, custom_bundle_price, overview_file, filename } =
          response.data;

        this.setState({
          quote,
          courses,
          filename,
          customBundlePrice: custom_bundle_price,
          overviewFile: overview_file,
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
      });
  };

  render() {
    const { overviewFile, filename } = this.state;

    return (
      <div className="ui pages teams-facelift" id="team_plans">
        <a href="#main" className="teams-sr-only">
          Skip to main content
        </a>
        <div className="teams-sr-only">
          <p>
            42courses for Teams is a corporate learning and development platform
            offering personalised Mini MBA courses in Behavioural Science,
            Artificial Intelligence, Creativity and Leadership. The platform is
            distinguished by its 80%+ team completion rate, human-marked
            assignments with 24-hour feedback, AI-powered personalisation and
            CPD-accredited certification.
          </p>
        </div>
        <SvgDefinitions />
        <main id="main">
          <HeroSection />
          <TrustedLogos />
          <CurriculumSection />
          <FeaturesSection />
          <CaseStudySection />
          <TestimonialsSection />
          <LeadGenSection
            overviewFile={overviewFile}
            fileName={filename}
            recaptchaRef={this.recaptchaRef}
            pathName={this.props.location?.pathname}
          />
          <QuickBuySection />
          <ComparisonSection />
          <FAQSection />
        </main>
      </div>
    );
  }
}
export default TeamPlans;
